import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props'
import { Facebook, Instagram } from '@mui/icons-material'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import axios from 'axios'

import signifyLogoTransp from './images/signify_logo_transparent_black.png'
import threatMatrixLogoTransp from './images/Threat_Matrix_Logo.png'
import wta from './images/wta.png'
import itf from './images/itf-l.png'
import usta from './images/usta-l.png'
import rolandGarros from './images/roland-garros-s.png'
import wimbledon from './images/wimbledon-l.png'
import epcr from './images/epcr-l.png'
import worldrugby from './images/worldrugby.png'

const Login = ({ setFbResponse, setFbDetails }) => {
  const [isLoggingIn, setIsLoggingIn] = React.useState(false)
  const [loginError, setLoginError] = React.useState('')
  const facebookAttempt = React.useRef(null)

  const defaultBackendUrl =
    typeof window !== 'undefined' ? window.location.origin : ''
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || defaultBackendUrl
  const isInstagramLoginEnabled =
    String(process.env.REACT_APP_ENABLE_IG_LOGIN || '').toLowerCase() === 'true'
  const redirectUri =
    process.env.REACT_APP_FACEBOOK_REDIRECT_URI ||
    (typeof window !== 'undefined' ? `${window.location.origin}/` : '/')
  const location = useLocation()

  const sendAuthDebug = React.useCallback(
    async (event, details = {}) => {
      try {
        await axios.post(`${backendUrl}/debug/client-auth-event`, {
          event,
          details,
        }, { withCredentials: true })
      } catch {}
    },
    [backendUrl],
  )

  // 1. Local‐storage check (identical in every variant)
  useEffect(() => {
    const now = Date.now()
    const setupTime = localStorage.getItem('setupTime')

    if (setupTime) {
      const hours = 1
      if (now - Number(setupTime) > hours * 60 * 60 * 1000) {
        localStorage.clear()
        localStorage.setItem('setupTime', now)
      } else {
        const localUserData = localStorage.getItem('userData')
        if (localUserData) {
          const savedUser = JSON.parse(localUserData)
          setFbDetails([savedUser])
        }
      }
    } else {
      localStorage.setItem('setupTime', now)
    }
  }, [setFbDetails])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state')
    const error = params.get('error')
    const errorReason = params.get('error_reason')
    const errorDescription = params.get('error_description')
    const hasOAuthSignal = Boolean(
      code || state || error || errorReason || errorDescription,
    )
    if (!hasOAuthSignal) return

    sendAuthDebug('oauth_query_on_client', {
      path: window.location.pathname,
      hasCode: Boolean(code),
      hasState: Boolean(state),
      error: error || null,
      errorReason: errorReason || null,
      errorDescription: errorDescription || null,
      userAgent:
        typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    })
  }, [sendAuthDebug])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const igLoginStatus = params.get('ig_login')
    if (!igLoginStatus) return
    if (igLoginStatus === 'success') return
    const statusToMessage = {
      error: 'Instagram authorization failed. Please try again.',
      state_error: 'Instagram authorization expired. Please start login again.',
      callback_error: 'Instagram sign-in could not be completed. Please retry.',
    }
    setLoginError(
      statusToMessage[igLoginStatus] ||
        'Instagram sign-in failed. Please try again.',
    )
  }, [])

  // 2. Facebook response handler (identical in every variant)
  const responseFacebook = async (response) => {
    await facebookAttempt.current
    await sendAuthDebug('facebook_callback_received', {
      status: response?.status || null,
      hasAccessToken: Boolean(response?.accessToken),
      hasUserId: Boolean(response?.userID),
      path: location.pathname,
    })

    if (!response || response.status === 'unknown' || !response.accessToken) {
      await sendAuthDebug('facebook_callback_invalid', {
        status: response?.status || null,
        hasAccessToken: Boolean(response?.accessToken),
        path: location.pathname,
      })
      setLoginError(
        'Facebook could not complete sign-in. Please close any in-app browser and try again in a normal browser.',
      )
      return
    }

    setLoginError('')

    try {
      setIsLoggingIn(true)
      sendAuthDebug('facebook_login_request_start', {
        userID: response.userID,
        path: location.pathname,
      })
      const loginResponse = await axios.post(
        `${backendUrl}/login`,
        {
          accessToken: response.accessToken,
          userID: response.userID,
          name: response.name,
          email: response.email,
          profilePictureUrl: response.picture?.data?.url || null,
          project: location.pathname.substring(1) || 'none',
        },
        { withCredentials: true },
      )

      sendAuthDebug('facebook_login_request_success', {
        userID: response.userID,
        appUserId: loginResponse?.data?.user?.id || null,
      })
      setFbDetails(loginResponse.data.user)
      localStorage.setItem('userData', JSON.stringify(loginResponse.data.user))
    } catch (error) {
      sendAuthDebug('facebook_login_request_error', {
        message: error?.message || null,
        code: error?.code || null,
        status: error?.response?.status || null,
        responseError: error?.response?.data?.error || null,
      })
      setLoginError(
        error?.response?.data?.error || 'Sign-in completed but app login failed. Please try again.',
      )
    } finally {
      setIsLoggingIn(false)
    }
  }

  const handleFacebookFailure = async (error) => {
    await facebookAttempt.current
    await sendAuthDebug('facebook_failure_callback', {
      message: error?.message || null,
      status: error?.status || null,
      errorSummary:
        typeof error === 'string'
          ? error
          : error?.error || error?.type || 'unknown',
      path: location.pathname,
    })
    setLoginError(
      'Facebook login was cancelled or blocked. Please retry in a normal browser.',
    )
  }

  const handleInstagramLogin = () => {
    const project = location.pathname.substring(1) || 'none'
    const startUrl = `${backendUrl}/auth/instagram/start?project=${encodeURIComponent(project)}`
    setLoginError('')
    window.location.assign(startUrl)
  }

  // 3. Instagram response handler (identical in every variant)
  const responseInstagram = async (response) => {
    if (response.status === 'unknown') {
      window.location.reload(false)
      return
    }

    try {
      const tokenExchangeRes = await axios.post(
        `${backendUrl}/exchange-token`,
        {
          userId: response.userID,
          shortLivedToken: response.accessToken,
        },
      )

      const longLivedToken = tokenExchangeRes.data.longLivedToken
      const tokenExpiry = tokenExchangeRes.data.tokenExpiry

      const pageTokenRes = await axios.get(
        `https://graph.facebook.com/18.0/${response.userID}/accounts?access_token=${response.accessToken}`,
      )
      const savedUser = await axios.post(`${backendUrl}/users`, {
        name: response.name,
        userId: response.userID,
        accessToken: response.accessToken,
        longLivedToken,
        tokenExpiry,
        profilepictureurl: response.picture.data.url,
        pages: pageTokenRes.data.data,
      })

      setFbDetails(savedUser.data)
      localStorage.setItem('userData', JSON.stringify(savedUser.data))
    } catch {}
  }

  // 4. Twitter login (identical in every variant)
  const handleTwitterLogin = () => {
    window.location.replace(`${backendUrl}/auth/twitter`)
  }

  // 5. Configuration of per‐page titles and logos
  const layoutConfig = {
    tennis: {
      mainTitle: 'Tennis Social Media Abuse Support Service',
      logoRows: [
        [wta, itf, usta],
        [wimbledon, rolandGarros],
      ],
    },
    rugby: {
      mainTitle: 'Rugby Social Media Abuse Support Service',
      logoRows: [[epcr]],
    },
    worldrugby: {
      mainTitle: 'Rugby Social Media Abuse Support Service',
      logoRows: [[worldrugby]],
    },
    default: {
      mainTitle: 'Social Media Abuse Support Service',
      logoRows: [], // no sport-specific logos
    },
  }

  // Determine which “page key” to use
  // e.g. location.pathname = '/tennis' → pageKey = 'tennis'
  const rawKey = location.pathname.replace('/', '')
  const pageKey = layoutConfig[rawKey] ? rawKey : 'default'
  const { mainTitle, logoRows } = layoutConfig[pageKey]

  // 6. Single consolidated return
  return (
    <>
      <Grid container justifyContent="center" alignItems="center" spacing={2}>
        <Grid item xs={12} md={8} lg={5}>
          <div className="text-box">
            <div className="login-container">
              <h1>{mainTitle}</h1>

              {logoRows.length > 0 && (
                <div className="logo-container">
                  {logoRows.map((row, rowIndex) => (
                    <div className="logo-pair" key={rowIndex}>
                      {row.map((logoSrc) => (
                        <img src={logoSrc} alt="" key={logoSrc} />
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <h1 className="login-heading">
              Sign-Up for Direct Messaging support, provided by:
            </h1>
            <div className="centered-logo">
              <img
                src={threatMatrixLogoTransp}
                alt="Threat Matrix Logo"
                className="smaller-logo"
              />
            </div>

            <p className="login-subheading bold-text" style={{ textAlign: 'center' }}>
              Welcome to the sign-up for automated protection of your Direct
              Messages on Meta (Facebook and Instagram) and/or X.
            </p>


            <p className="login-text" style={{ textAlign: 'center' }}>
              To authorize this service, please log in using the sign-in with
              Facebook or X button below and follow the prompts.
            </p>

            <p className="login-text" style={{ textAlign: 'center' }}>
              For questions and help please email ThreatMatrix@Signify.ai.
            </p>
          </div>
        </Grid>

        {/* Facebook button */}
        <Grid item xs={12}>
          <div className="facebook-button-container">
            <FacebookLogin
              appId={process.env.REACT_APP_FACEBOOK_APP_ID}
              scope="business_management,pages_messaging,pages_read_engagement,pages_manage_metadata,instagram_basic,instagram_manage_messages"
              redirectUri={redirectUri}
              disableMobileRedirect
              isMobile={false}
              cookie
              fields="name,email,picture"
              callback={responseFacebook}
              onFailure={handleFacebookFailure}
              render={(renderProps) => (
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#1877F2',
                    color: 'white',
                    padding: '10px 20px',
                    fontSize: '14px',
                  }}
                  startIcon={<Facebook />}
                  onClick={(event) => {
                    setLoginError('')
                    facebookAttempt.current = axios.post(
                      `${backendUrl}/auth/facebook/start`,
                      { project: location.pathname.substring(1) || 'none' },
                      { withCredentials: true },
                    ).catch(() => {
                      setLoginError('Unable to record login attempt. Please retry if sign-in fails.')
                    })
                    renderProps.onClick(event)
                  }}
                  disabled={isLoggingIn}
                >
                  Sign in with Facebook
                </Button>
              )}
            />
          </div>
          {isInstagramLoginEnabled && (
            <div
              className="instagram-button-container"
              style={{ marginTop: '10px', textAlign: 'center' }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#E1306C',
                  color: 'white',
                  padding: '10px 20px',
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: '#c1275b',
                  },
                }}
                startIcon={<Instagram />}
                onClick={handleInstagramLogin}
                disabled={isLoggingIn}
              >
                Sign in with Instagram
              </Button>
            </div>
          )}
          {isLoggingIn && (
            <div
              style={{
                marginTop: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <CircularProgress size={18} />
              <p className="login-smalltext">Connecting pages and accounts…</p>
            </div>
          )}
          {!isLoggingIn && loginError && (
            <p
              className="login-smalltext"
              style={{ marginTop: '10px', color: '#b3261e', textAlign: 'center' }}
            >
              {loginError}
            </p>
          )}
        </Grid>

        {/* Twitter button (now in every layout) */}
        <Grid item xs={12}>
          <div
            className="twitter-button-container"
            style={{ textAlign: 'center', marginTop: '16px' }}
          >
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#1DA1F2',
                color: 'white',
                padding: '10px 20px',
                fontSize: '14px',
              }}
              onClick={handleTwitterLogin}
            >
              Sign in with Twitter
            </Button>
          </div>
        </Grid>

        {/* Powered by Signify logo (identical in every variant) */}
        <Grid item xs={12}>
          <div className="login-container">
            <p className="login-smalltext">Powered by:</p>
            <img
              src={signifyLogoTransp}
              alt="Signify Logo"
              className="login-logo"
            />
            <p className="login-smalltext" style={{ marginTop: '8px' }}>
              <a
                href="https://www.signify.ai/threat-matrix-app-privacy"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>
              {' • '}
              <a
                href="https://www.signify.ai/threat-matrix-terms-of-service"
                target="_blank"
                rel="noreferrer"
              >
                Terms of Service
              </a>
            </p>
          </div>
        </Grid>
      </Grid>
    </>
  )
}

export default Login
